import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useGuestIdentity } from '../utils/useGuestIdentity';
import InspoEmailPrompt from './InspoEmailPrompt';

const ONB_KEY_PREFIX = 'crystal_inspo_onb_';     // per-section onboarding tooltip
const PROMPTED_KEY = 'crystal_inspo_email_prompted';

const readLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

/**
 * FavouriteButton — site-wide heart icon.
 *
 * Tap toggles fave instantly (localStorage). After tap:
 *  - Fires a global `crystal-save` CustomEvent so the SaveToastHost can show
 *    the "Saved to your inspo · Add to list" toast.
 *  - The first time a visitor hearts something in each section (e.g. 'photo',
 *    'film', 'inspo'), shows a one-time tooltip explaining the feature.
 *  - The very first heart by an un-registered visitor (across the whole site)
 *    surfaces a tiny email-only sign-in prompt.
 *
 * Variants: "light" (white over imagery), "dark" (black over white), "ghost" (text-only).
 */
const FavouriteButton = ({
  itemType,
  itemId,
  size = 18,
  variant = 'light',
  className = '',
  label,
  stop = true,
  onChange,
  testId,
}) => {
  const { isFav, toggleFav, isRegistered } = useGuestIdentity();
  const active = isFav(itemType, itemId);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showOnb, setShowOnb] = useState(false);

  // Auto-hide onboarding tooltip after 4s
  useEffect(() => {
    if (!showOnb) return;
    const t = setTimeout(() => setShowOnb(false), 4000);
    return () => clearTimeout(t);
  }, [showOnb]);

  const handleClick = async (e) => {
    if (stop) { e.preventDefault(); e.stopPropagation(); }
    const nowFav = await toggleFav(itemType, itemId);
    if (typeof onChange === 'function') onChange(nowFav);

    // Fire global save toast event so the host can show the picker CTA
    try {
      window.dispatchEvent(new CustomEvent('crystal-save', {
        detail: { itemType, itemId, nowFav }
      }));
    } catch { /* ignore */ }

    // Per-section onboarding tooltip — only first time on each section
    const onbKey = ONB_KEY_PREFIX + itemType;
    if (nowFav && !readLS(onbKey)) {
      writeLS(onbKey, '1');
      setShowOnb(true);
    }

    // Email prompt — one-time across the site, only for unregistered guests
    if (nowFav && !isRegistered && readLS(PROMPTED_KEY) !== '1') {
      writeLS(PROMPTED_KEY, '1');
      // Slight delay so the toast appears first, then the modal
      setTimeout(() => setShowPrompt(true), 600);
    }
  };

  // Pre-compute class strings — Tailwind JIT needs them whole, not built per-state at runtime
  const base = "inline-flex items-center justify-center gap-2 select-none transition-all duration-200 group";
  const chrome = {
    light: active
      ? 'bg-white/95 text-red-500 border border-white shadow-md'
      : 'bg-black/35 text-white border border-white/40 backdrop-blur-sm hover:bg-black/55',
    dark: active
      ? 'bg-black text-red-500 border border-black'
      : 'bg-white text-black/70 border border-black/15 hover:border-black',
    ghost: active
      ? 'text-red-500'
      : 'text-white/70 hover:text-white',
  }[variant];
  const sizing = variant === 'ghost'
    ? 'w-9 h-9'
    : 'w-10 h-10 sm:w-11 sm:h-11';
  const labelClass = label ? 'px-3 sm:px-4 w-auto h-9 sm:h-10' : '';

  return (
    <>
      <span className="relative inline-block">
        <button
          type="button"
          data-testid={testId || `fav-${itemType}-${itemId}`}
          onClick={handleClick}
          aria-pressed={active}
          aria-label={active ? 'Remove from your inspo' : 'Save to your inspo'}
          className={`${base} ${chrome} ${label ? labelClass : sizing} ${className}`}
          style={{ touchAction: 'manipulation' }}
        >
          <Heart
            size={size}
            strokeWidth={1.75}
            fill={active ? 'currentColor' : 'none'}
          />
          {label ? <span className="text-[10px] tracking-[0.18em] uppercase font-medium">{label}</span> : null}
        </button>

        {/* Hover "Save" label — desktop only, not shown if button already has a label */}
        {!label && (
          <span
            className="hidden sm:block absolute top-full mt-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-black text-white text-[9px] tracking-[0.2em] uppercase whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
            aria-hidden="true"
          >
            {active ? 'Saved' : 'Save'}
          </span>
        )}

        {/* One-time onboarding tooltip per section */}
        {showOnb && (
          <span
            role="status"
            data-testid={`fav-onboarding-${itemType}`}
            className="absolute top-full mt-2 right-0 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 px-3 py-2.5 bg-black text-white text-[11px] tracking-wide w-[210px] sm:w-[230px] shadow-2xl"
            style={{ animation: 'onbIn 320ms cubic-bezier(0.22, 1, 0.36, 1) both' }}
          >
            <span className="block font-medium mb-0.5 text-[10px] tracking-[0.2em] uppercase text-white/55">Inspo</span>
            Tap to save inspiration for your wedding. Build a board, share it with us.
            <span className="absolute -top-1.5 right-3 sm:left-1/2 sm:-translate-x-1/2 w-3 h-3 bg-black rotate-45" />
            <style>{`@keyframes onbIn{0%{opacity:0;transform:translateY(-6px)}100%{opacity:1;transform:translateY(0)}}`}</style>
          </span>
        )}
      </span>

      <InspoEmailPrompt
        isOpen={showPrompt}
        onClose={() => setShowPrompt(false)}
        onSuccess={() => setShowPrompt(false)}
        onSkip={() => setShowPrompt(false)}
      />
    </>
  );
};

export default FavouriteButton;
